/**
 * Colour assignment for AI-drawn charts.
 *
 * In the rest of the product a chart colour often MEANS something: `stableColorGenerator` pins Equity
 * to the same blue and Fixed Income to the same yellow everywhere they appear, so the reader learns
 * the mapping once. A chart drawn from a model's `plot_chart` spec had no access to that — it coloured
 * purely by arrival order, so an asset-class breakdown from the assistant contradicted every other
 * view of the same data.
 *
 * So: a slot whose label the product already has a colour for takes that colour, and everything else
 * draws from the SAME sequence the product's own charts use for unnamed series (`colorGenerator`).
 *
 * Deliberately not the design system's `global_palette_chart_*` tokens, though those would be the
 * purer citation. Two files in the app render from them; fourteen render from `colorGenerator`. A
 * chart that cites the tokens is a chart that doesn't match the product — its first series comes out
 * royal blue where every other chart in the app opens on `#00AEEF` — and it would put two colour
 * spaces in one picture, one resolved by the browser from custom properties and one from literals,
 * which is exactly what makes a duplicate impossible to detect. Reconciling the token set with the
 * list below is real work, but it is design-system work, and until it happens the assistant matching
 * the product beats the assistant being correct on its own.
 *
 * Pure and free of Highcharts, so it can be tested on its own.
 */
import { colorGenerator, knownStableColor } from "$root/utils";

/**
 * How deep `colorGenerator` stays deterministic: 10 base colours × 4 progressively darker levels.
 * Past that it returns a RANDOM colour, which would make this module impure and its charts
 * unrepeatable across renders — so fallbacks wrap instead. A chart with 40 unnamed series has worse
 * problems than a repeated hue.
 */
const DETERMINISTIC_COLORS = 40;

/** Case-insensitive: the curated table stores `#00AEEF`, `colorGenerator` emits `#00aeef`. */
function sameColor(a: string, b: string): boolean {
	return a.toLowerCase() === b.toLowerCase();
}

/**
 * Colours for a chart's slots, in the order the labels are given.
 *
 * A "slot" is whatever carries a colour for the chart type at hand: one per series for
 * line/area/column/bar/scatter, one per data point for pie. The caller decides which and passes the
 * corresponding labels.
 *
 * Labels the product has a colour for are resolved first; the remaining slots then walk
 * `colorGenerator` from the start, SKIPPING anything already taken. Two properties fall out of that
 * order, and both matter:
 *
 * - No slot repeats another's colour. This is only possible because both sides are now hex literals.
 * - Named slots don't consume the sequence. With `["Equity", "Other"]`, `Other` gets the sequence's
 *   FIRST unused colour rather than its second — nothing is silently burned, and a reader isn't
 *   asked to notice that a gap in the palette encodes something.
 */
export function assignChartColors(labels: ReadonlyArray<string>): string[] {
	const semantic = labels.map((label) => knownStableColor(label));
	const taken = semantic.filter((color): color is string => color !== undefined);

	let cursor = 0;
	const nextUnused = (): string => {
		for (let step = 0; step < DETERMINISTIC_COLORS; step++) {
			const candidate = colorGenerator(cursor++ % DETERMINISTIC_COLORS);
			if (!taken.some((used) => sameColor(used, candidate))) {
				taken.push(candidate);
				return candidate;
			}
		}
		// Every colour in the sequence is spoken for: wrap rather than invent one.
		return colorGenerator(cursor++ % DETERMINISTIC_COLORS);
	};

	return semantic.map((color) => color ?? nextUnused());
}
